import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { redtailApi, queryKeys, Redtail } from '@client/api/integrations';
import { selectClients } from '@client/model/selectors';
import { clientCanViewResults } from '@client/components/ClientsTable';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

interface LinkClientModalProps {
  open: boolean;
  onClose: () => void;
  contact: Redtail.Contact | null;
}

export function LinkClientModal({ open, onClose, contact }: LinkClientModalProps) {
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const clients = useSelector(selectClients);
  const queryClient = useQueryClient();

  const linkMutation = useMutation({
    mutationFn: (data: Redtail.LinkContactRequest) => redtailApi.contacts.link(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.redtail.lists('contacts') });
      handleClose();
    },
  });

  const handleClose = () => {
    setSelectedClientId(null);
    setComboboxOpen(false);
    linkMutation.reset();
    onClose();
  };

  const handleLink = () => {
    if (!contact || !selectedClientId) return;
    linkMutation.mutate({
      contactId: contact.id,
      clientId: selectedClientId,
    });
  };

  const eligibleClients = clients.filter(client => clientCanViewResults(client.clientTags));

  const selectedClient = eligibleClients.find(c => c.clientId === selectedClientId);
  const selectedClientName = selectedClient
    ? `${selectedClient.clientFirstName ?? ''} ${selectedClient.clientLastName ?? ''}`.trim() ||
      'Unnamed Client'
    : null;

  const contactDisplayName = contact
    ? `${contact.firstName ?? contact.companyName ?? ''} ${contact.lastName ?? ''}`.trim()
    : '';

  return (
    <Dialog
      open={open}
      onOpenChange={open => !open && !linkMutation.isPending && handleClose()}
    >
      <DialogContent className="sm:max-w-lg gap-0 !p-0">
        <div className="p-5">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Link Contact</DialogTitle>
            <DialogDescription>
              Link <span className="font-medium text-gray-900">{contactDisplayName}</span> to an
              existing Waterlily client.
            </DialogDescription>
          </DialogHeader>

          <div className="pt-4">
          <Popover
            open={comboboxOpen}
            onOpenChange={setComboboxOpen}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={comboboxOpen}
                className="w-full justify-between font-normal"
              >
                {selectedClientName ?? 'Select a client...'}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[--radix-popover-trigger-width] p-0"
              align="start"
            >
              <Command>
                <CommandInput placeholder="Search clients..." />
                <CommandList>
                  <CommandEmpty>No clients found.</CommandEmpty>
                  <CommandGroup>
                    {eligibleClients.map(client => {
                      const fullName =
                        `${client.clientFirstName ?? ''} ${client.clientLastName ?? ''}`.trim() ||
                        'Unnamed Client';
                      const isSelected = selectedClientId === client.clientId;

                      return (
                        <CommandItem
                          key={client.clientId}
                          value={`${fullName} ${client.clientEmail ?? ''}`}
                          onSelect={() => {
                            setSelectedClientId(isSelected ? null : client.clientId);
                            setComboboxOpen(false);
                          }}
                        >
                          <Check
                            className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
                          />
                          <div className="flex flex-col">
                            <span>{fullName}</span>
                            {client.clientEmail && (
                              <span className="text-xs text-gray-500">{client.clientEmail}</span>
                            )}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

            {linkMutation.isError && (
              <p className="mt-2 text-sm text-red-600">
                {linkMutation.error?.message || 'Failed to link contact'}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="border-t border-gray-200 p-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={linkMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLink}
            disabled={!selectedClientId || linkMutation.isPending}
          >
            {linkMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Linking...
              </>
            ) : (
              'Link'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
